import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCabinet } from '../contexts/CabinetContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getNavItems } from './navigation/navItems';
import { ThemeToggle } from './ui/ThemeToggle';
import { LanguageToggle } from './ui/LanguageToggle';
import { BrandMark } from './ui/BrandMark';
import { getImageUrl } from '../utils/image';
import { getCompleteness } from '../utils/completeness';

const DAY_MS = 1000 * 60 * 60 * 24;
/** Дальше месяца срок подписки — не новость, плашку не показываем. */
const NOTICE_WINDOW_DAYS = 30;
/** Ближе недели — уже срочно. */
const URGENT_DAYS = 7;

/** Инициалы для аватара. Кавычки и скобки отбрасываем: у «Барбершоп
    "Пила"» инициалы — БП, а не Б«. */
function initials(name?: string): string {
  if (!name) return '—';
  const words = name.split(/[\s·—-]+/).map((word) => word.replace(/[^\p{L}\p{N}]/gu, ''));
  const letters = words.filter(Boolean).slice(0, 2).map((word) => word[0].toUpperCase());
  return letters.join('') || '—';
}

/**
 * Шапка кабинета, дизайн 2.0.
 *
 * Два яруса вместо рельса слева: верхний — кто я и что ищу, нижний —
 * где я и насколько всё готово. Рельс убран потому, что в нём было
 * четыре пункта на 92 px ширины — цена, которую полотно платило за
 * навигацию, не окупалась.
 *
 * На узком экране нижний ярус скрыт (см. design2.css), разделы там
 * остаются в нижней панели — до неё дотягивается палец.
 */
export const AppHeader: React.FC = () => {
  const { partner, logout } = useAuth();
  const { query, setQuery, stats } = useCabinet();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMenuOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setIsMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isMenuOpen]);

  // Меню закрываем на переходе: иначе оно висит поверх нового экрана.
  useEffect(() => setIsMenuOpen(false), [location.pathname]);

  const completeness = useMemo(
    () => getCompleteness(partner ?? null),
    [partner],
  );

  const avatarUrl = partner?.main_image ? getImageUrl(partner.main_image.url) || partner.main_image.url : null;

  const sectionNote = (() => {
    if (location.pathname.startsWith('/catalog')) {
      return `${stats?.menu_categories_count ?? 0} ${t.cabinet.categoryNote}`;
    }
    if (location.pathname.startsWith('/discounts')) {
      return `${stats?.discounts_count ?? 0} ${t.cabinet.discountNote}`;
    }
    if (location.pathname.startsWith('/promo')) return t.cabinet.storiesAndBanner;
    if (location.pathname.startsWith('/billing')) return t.cabinet.subscription;
    return t.cabinet.venueProfile;
  })();

  const subscriptionChip = (() => {
    const sub = partner?.subscription;
    if (!partner) return null;
    // Неактивированная подписка уже объяснена карточкой активации выше.
    if (sub?.is_activated === false) return null;

    const expiresAt = sub?.expires_at ?? partner.expires_at ?? null;
    const daysLeft =
      sub?.days_left ?? (expiresAt ? Math.floor((new Date(expiresAt).getTime() - Date.now()) / DAY_MS) : null);
    if (daysLeft === null || daysLeft > NOTICE_WINDOW_DAYS) return null;

    const tone = daysLeft < 0 ? 'bad' : daysLeft <= URGENT_DAYS ? 'warn' : '';
    const text =
      daysLeft < 0
        ? t.cabinet.subAccessClosed
        : daysLeft === 0
          ? t.cabinet.subClosesToday
          : `${t.cabinet.subClosesIn} ${daysLeft} ${t.common.day}`;

    return (
      <Link
        to="/billing"
        className={`qp-status${tone ? ` qp-status--${tone}` : ''}`}
        style={{ textDecoration: 'none' }}
      >
        {text}
      </Link>
    );
  })();

  const navItems = useMemo(() => getNavItems(t), [t]);

  return (
    <header className="qp-topbar">
      <div className="qp-topbar-row">
        <Link to="/" className="qp-brand" aria-label="QPrice — на главную">
          <span className="qp-brand-tile">
            <BrandMark />
          </span>
          <span className="qp-brand-text">
            <span className="qp-brand-name">QPrice</span>
            <span className="qp-brand-sub">{t.auth.choiceTitle.toUpperCase()}</span>
          </span>
        </Link>

        <span className="qp-topbar-sep" aria-hidden />

        <Link to="/" className="qp-venue" aria-label={t.cabinet.venueProfile}>
          <span
            className="qp-venue-avatar"
            style={avatarUrl ? { backgroundImage: `url("${avatarUrl}")` } : undefined}
            aria-hidden
          />
          <span className="qp-venue-body">
            <span className="qp-venue-name">{partner?.name || '—'}</span>
            <span className="qp-venue-meta">{partner?.address || partner?.city?.name || '—'}</span>
          </span>
        </Link>

        <div className="qp-search" role="search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" strokeLinecap="round" />
          </svg>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t.common.searchPlaceholder}
            aria-label={t.common.search}
          />
        </div>

        <div className="qp-topbar-actions">
          {subscriptionChip}
          <LanguageToggle />
          <ThemeToggle />
          <button
            type="button"
            className="qp-btn qp-btn--primary"
            onClick={() => navigate('/discounts?new=1')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
              <path d="M12 5v14M5 12h14" strokeLinecap="round" />
            </svg>
            {t.cabinet.createDiscount}
          </button>

          <div ref={menuRef} style={{ position: 'relative' }}>
            <button
              type="button"
              className="qp-avatar"
              onClick={() => setIsMenuOpen((open) => !open)}
              aria-haspopup="menu"
              aria-expanded={isMenuOpen}
              aria-label={t.nav.accountMenu}
              style={{ cursor: 'pointer' }}
            >
              {initials(partner?.name)}
            </button>
            {isMenuOpen && (
              <div
                role="menu"
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 'calc(100% + 8px)',
                  minWidth: 200,
                  padding: 6,
                  borderRadius: 12,
                  border: '1px solid var(--qp2-line)',
                  background: 'var(--qp2-surface)',
                  boxShadow: 'var(--qp2-shadow-lift)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  zIndex: 20,
                }}
              >
                <button type="button" role="menuitem" className="qp-menu-item" onClick={() => navigate('/edit')}>
                  {t.nav.editProfile}
                </button>
                <button type="button" role="menuitem" className="qp-menu-item" onClick={() => navigate('/billing')}>
                  {t.nav.billing}
                </button>
                <span style={{ height: 1, background: 'var(--qp2-line-soft)', margin: '4px 0' }} />
                <button
                  type="button"
                  role="menuitem"
                  className="qp-menu-item qp-menu-item--danger"
                  onClick={() => {
                    logout();
                    navigate('/login');
                  }}
                >
                  {t.nav.logout}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="qp-topbar-row qp-topbar-row--sub">
        <nav className="qp-tabs" aria-label={t.nav.ariaTopTabs}>
          {navItems.map((item) => {
            const active =
              item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path);
            const badge =
              item.path === '/catalog'
                ? stats?.menu_categories_count
                : item.path === '/discounts'
                  ? stats?.discounts_count
                  : undefined;
            return (
              <button
                key={item.path}
                type="button"
                className="qp-tab"
                aria-current={active ? 'page' : undefined}
                onClick={() => navigate(item.path)}
              >
                {item.icon}
                <span>{item.label}</span>
                {badge ? <span className="qp-tab-badge">{badge}</span> : null}
              </button>
            );
          })}
        </nav>

        <div className="qp-strip">
          <span className="qp-strip-text">{sectionNote}</span>
          <span className="qp-topbar-sep" aria-hidden />
          <span className="qp-strip-text">{t.cabinet.profileFilled} {completeness.percent}%</span>
          <span
            className="qp-meter"
            role="progressbar"
            aria-valuenow={completeness.percent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={t.cabinet.ariaProfileCompleteness}
          >
            <span style={{ width: `${completeness.percent}%` }} />
          </span>
          <Link to="/edit" className="qp-link">
            {t.cabinet.whatToImprove}
          </Link>
        </div>
      </div>
    </header>
  );
};
